'use client';

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  /** CSS colour string. Use `theme.colors.status.*` or `theme.colors.activity.*`. */
  color: string;
}

export interface StatusDonutProps {
  segments: DonutSegment[];
  /** Outer diameter in px. Default 120. */
  size?: number;
  /** Ring thickness in px. Default 22. */
  thickness?: number;
  /** Text in the donut hole (e.g. total count). */
  innerLabel?: string;
  /** Smaller line below innerLabel (e.g. "total"). */
  innerSubLabel?: string;
  /** Called when a segment or legend row is activated. */
  onSegmentClick?: (segment: DonutSegment) => void;
  /** Show legend below the donut. Default true. */
  showLegend?: boolean;
  className?: string;
}

const Root = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const LegendRow = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.xs};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.xs};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
`;

const LegendDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => p.$color};
`;

const LegendValue = styled.span`
  margin-left: auto;
  font-family: ${(p) => p.theme.fontFamily.mono};
  font-size: ${(p) => p.theme.fontSize.xs};
  color: ${(p) => p.theme.colors.neutral.ink};
`;

function CenterLabel({
  cx,
  cy,
  innerLabel,
  innerSubLabel,
  fontSize,
}: {
  cx: number;
  cy: number;
  innerLabel: string;
  innerSubLabel?: string;
  fontSize: number;
}) {
  return (
    <>
      <text
        x={cx}
        y={cy - (innerSubLabel ? fontSize * 0.4 : 0)}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={fontSize}
        fontWeight={700}
        fill="currentColor"
      >
        {innerLabel}
      </text>
      {innerSubLabel && (
        <text
          x={cx}
          y={cy + fontSize * 0.85}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fontSize * 0.62}
          fill="currentColor"
          opacity={0.55}
        >
          {innerSubLabel}
        </text>
      )}
    </>
  );
}

/**
 * Segmented donut chart with optional legend and click interaction.
 *
 * Colours are provided by the caller — use `theme.colors.activity.*` or
 * `theme.colors.status.*` to stay on-token.
 *
 * @example
 * <StatusDonut
 *   segments={[
 *     { key: 'active', label: 'Active', value: 8, color: theme.colors.status.active.dot },
 *     { key: 'done',   label: 'Done',   value: 5, color: theme.colors.status.done.dot },
 *   ]}
 *   innerLabel="13"
 *   innerSubLabel="trips"
 * />
 */
export function StatusDonut({
  segments,
  size = 120,
  thickness = 22,
  innerLabel,
  innerSubLabel,
  onSegmentClick,
  showLegend = true,
  className,
}: StatusDonutProps) {
  const nonEmpty = segments.filter((s) => s.value > 0);
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2;
  const innerR = outerR - thickness;
  const fontSize = size * 0.16;

  const ariaLabel = segments.length
    ? segments.map((s) => `${s.label} ${s.value}`).join(', ')
    : 'No data';

  return (
    <Root className={className}>
      <div role="img" aria-label={ariaLabel} style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {nonEmpty.length === 0 ? (
              <Pie
                data={[{ value: 1 }]}
                cx="50%"
                cy="50%"
                innerRadius={innerR}
                outerRadius={outerR}
                dataKey="value"
                stroke="none"
                isAnimationActive={false}
              >
                <Cell fill="currentColor" fillOpacity={0.12} />
              </Pie>
            ) : (
              <Pie
                data={nonEmpty}
                cx="50%"
                cy="50%"
                innerRadius={innerR}
                outerRadius={outerR}
                dataKey="value"
                paddingAngle={nonEmpty.length > 1 ? 2 : 0}
                stroke="none"
                isAnimationActive={false}
                onClick={(entry) => onSegmentClick?.(entry as unknown as DonutSegment)}
                style={{ cursor: onSegmentClick ? 'pointer' : 'default' }}
              >
                {nonEmpty.map((seg) => (
                  <Cell
                    key={seg.key}
                    fill={seg.color}
                    role={onSegmentClick ? 'button' : undefined}
                    aria-label={onSegmentClick ? seg.label : undefined}
                    tabIndex={onSegmentClick ? 0 : undefined}
                    onKeyDown={
                      onSegmentClick
                        ? (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onSegmentClick(seg);
                            }
                          }
                        : undefined
                    }
                  />
                ))}
              </Pie>
            )}
            {innerLabel && (
              <CenterLabel
                cx={cx}
                cy={cy}
                innerLabel={innerLabel}
                innerSubLabel={innerSubLabel}
                fontSize={fontSize}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
      {showLegend && segments.length > 0 && (
        <Legend>
          {segments.map((seg) => (
            <LegendRow
              key={seg.key}
              $clickable={!!onSegmentClick}
              onClick={() => onSegmentClick?.(seg)}
            >
              <LegendDot $color={seg.color} />
              <span>{seg.label}</span>
              <LegendValue>{seg.value}</LegendValue>
            </LegendRow>
          ))}
        </Legend>
      )}
    </Root>
  );
}
